import { startOfDay, endOfDay, intervalToDuration } from 'date-fns';
import type { CSSProperties } from 'react';

type BadgeStyle = CSSProperties & {
    display: string;
    padding: string;
    borderRadius: string;
    fontSize: string;
    whiteSpace: string;
    backgroundColor: string;
    color: string;
};

const baseBadgeStyle: CSSProperties = {
    display: 'inline-block',
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
};

const defaultBadgeColors = {
    backgroundColor: '#e2e3e5',
    color: '#383d41',
};

// Helper function to get outcome badge background and text color
export const getOutcomeStyle = (outcome: string | null | undefined): BadgeStyle => {
    const style: CSSProperties = {
        ...baseBadgeStyle,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
    };

    if (!outcome) {
        return {
            ...style,
            ...defaultBadgeColors,
        } as BadgeStyle;
    }

    const outcomeLower = outcome.toLowerCase();

    switch (outcomeLower) {
        case 'pending':
            return {
                ...style,
                backgroundColor: '#cce5ff',
                color: '#004085',
            } as BadgeStyle;
        case 'completed':
            return {
                ...style,
                backgroundColor: '#d4edda',
                color: '#155724',
            } as BadgeStyle;
        case 'transfer_in_progress':
        case 'transfer_user_abandoned':
            return {
                ...style,
                backgroundColor: '#f0e6fa',
                color: '#6a359e',
            } as BadgeStyle;
        case 'failed':
        case 'blocked':
            return {
                ...style,
                backgroundColor: '#f8d7da',
                color: '#721c24',
            } as BadgeStyle;
        case 'in_progress':
            return {
                ...style,
                backgroundColor: '#e2f4fd',
                color: '#0c5460',
            } as BadgeStyle;
        case 'voicemail':
            return {
                ...style,
                backgroundColor: '#fcf3e3',
                color: '#f39c12',
            } as BadgeStyle;
        case 'transfer_completed':
            return {
                ...style,
                backgroundColor: '#e8f4fc',
                color: '#3498db',
            } as BadgeStyle;
        default:
            return {
                ...style,
                ...defaultBadgeColors,
            } as BadgeStyle;
    }
};

// Add formatDisposition function
export const formatDisposition = (disposition: string | null | undefined): string => {
    if (!disposition) return '';

    // Replace underscores with spaces and convert to Title Case
    return disposition
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Helper function to format stage display text
export const formatStageDisplay = (stage: string | null | undefined): string => {
    if (!stage) return '';

    // Replace underscores with spaces and convert to Title Case
    return stage
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Add getDispositionStyle function
export const getDispositionStyle = (disposition: string | null | undefined): BadgeStyle => {
    const style = { ...baseBadgeStyle };

    if (!disposition) {
        return {
            ...style,
            ...defaultBadgeColors,
        } as BadgeStyle;
    }

    const dispositionLower = disposition.toLowerCase();

    switch (dispositionLower) {
        case 'undetermined':
            return {
                ...style,
                backgroundColor: '#e2e3e5',
                color: '#383d41',
            } as BadgeStyle;
        case 'positive':
            return {
                ...style,
                backgroundColor: '#d4edda',
                color: '#155724',
            } as BadgeStyle;
        case 'negative':
            return {
                ...style,
                backgroundColor: '#f8d7da',
                color: '#721c24',
            } as BadgeStyle;
        case 'neutral':
            return {
                ...style,
                backgroundColor: '#fff3cd',
                color: '#856404',
            } as BadgeStyle;
        default:
            return {
                ...style,
                ...defaultBadgeColors,
            } as BadgeStyle;
    }
};

// Helper function to get lead status background and text color
// Supports both snake_case (legacy) and camelCase (PartyStages enum) formats
export const getLeadStatusStyle = (status: string | null | undefined): BadgeStyle => {
    const style = { ...baseBadgeStyle };

    if (!status) {
        return {
            ...style,
            ...defaultBadgeColors,
        } as BadgeStyle;
    }

    const statusLower = status.toLowerCase();

    switch (statusLower) {
        // New Lead - blue (camelCase: NewLead, snake_case: new_lead)
        case 'newlead':
        case 'new_lead':
            return {
                ...style,
                backgroundColor: '#cce5ff',
                color: '#004085',
            } as BadgeStyle;

        // Contacted/Qualified/Outreach - green
        case 'contacted':
        case 'qualified':
        case 'outreach':
        case 'openconsult':
        case 'open_consult':
            return {
                ...style,
                backgroundColor: '#d4edda',
                color: '#155724',
            } as BadgeStyle;

        // Attempt Made/Pitched Pending - yellow/warning
        case 'attempt_made':
        case 'pitchedpending':
        case 'pitched_pending':
            return {
                ...style,
                backgroundColor: '#fff3cd',
                color: '#856404',
            } as BadgeStyle;

        // Unreachable/Unable to Contact/Unqualified - red
        case 'unreachable':
        case 'unabletocontact':
        case 'unable_to_contact':
        case 'unqualified':
            return {
                ...style,
                backgroundColor: '#f8d7da',
                color: '#721c24',
            } as BadgeStyle;

        // Lost Opportunity/Existing Case Client - purple
        case 'lostopportunity':
        case 'lost_opportunity':
        case 'existingcaseclient':
        case 'existing_case_client':
            return {
                ...style,
                backgroundColor: '#f0e6fa',
                color: '#6a359e',
            } as BadgeStyle;

        default:
            return {
                ...style,
                ...defaultBadgeColors,
            } as BadgeStyle;
    }
};

// Helper function to format lead status for display
// Handles both snake_case and camelCase formats
export const formatLeadStatus = (status: string | null | undefined): string => {
    if (!status) return '';

    // Insert space before capital letters (for camelCase), then replace underscores with spaces
    return status
        .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase → camel Case
        .replace(/_/g, ' ') // snake_case → snake case
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase()); // Title Case
};

// Helper function to format call outcome for display
export const formatCallOutcome = (outcome: string | null | undefined): string => {
    if (!outcome) return '';
    return outcome
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Helper function to format SMS status for display
export const formatSMSStatus = (status: string | null | undefined): string => {
    if (!status) return '';
    return status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Helper function to format SMS disposition for display (e.g. OPT_IN -> "Opt in")
export const formatSMSDisposition = (disposition: string | null | undefined): string => {
    if (!disposition) return '';
    return disposition
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Helper function to get SMS status badge style
// Uses CSS variables from /common/styles/_variables.scss for consistency
export const getSMSStatusStyle = (status: string | null | undefined): BadgeStyle => {
    const style: CSSProperties = {
        ...baseBadgeStyle,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
    };

    if (!status) {
        return {
            ...style,
            ...defaultBadgeColors,
        } as BadgeStyle;
    }

    const statusLower = status.toLowerCase();

    switch (statusLower) {
        case 'pending':
        case 'queued':
            return {
                ...style,
                backgroundColor: 'var(--info-color-alt)',
                color: 'var(--info-color)',
            } as BadgeStyle;
        case 'sending':
            return {
                ...style,
                backgroundColor: 'var(--info-color-alt)',
                color: 'var(--info-color)',
            } as BadgeStyle;
        case 'sent':
            return {
                ...style,
                backgroundColor: 'var(--success-color-alt)',
                color: 'var(--success-color)',
            } as BadgeStyle;
        case 'user_engaged':
        case 'complete':
        case 'delivered':
            return {
                ...style,
                backgroundColor: 'var(--success-color-alt)',
                color: 'var(--success-color)',
            } as BadgeStyle;
        case 'user_abandoned':
        case 'agent_abandoned':
        case 'blocked_opt_out':
        case 'blocked':
        case 'undeliverable':
        case 'rate_limited':
        case 'system_error':
        case 'opt_out':
            return {
                ...style,
                backgroundColor: 'var(--warning-color-alt)',
                color: 'var(--warning-color)',
            } as BadgeStyle;
        case 'failed':
        case 'undelivered':
            return {
                ...style,
                backgroundColor: 'var(--error-color-alt)',
                color: 'var(--error-color)',
            } as BadgeStyle;
        case 'replied':
            return {
                ...style,
                backgroundColor: 'var(--bg-light-purple)',
                color: 'var(--primary-purple-dark)',
            } as BadgeStyle;
        case 'carrier_filtered':
        case 'invalid_number':
        case 'landline':
            return {
                ...style,
                backgroundColor: 'var(--warning-color-alt)',
                color: 'var(--warning-color)',
            } as BadgeStyle;
        default:
            return {
                ...style,
                ...defaultBadgeColors,
            } as BadgeStyle;
    }
};

export type DateTimeRange = {
    startWithTime: Date;
    endWithTime: Date;
};

// Function to set date times properly with timezone consideration using date-fns
export const setDateTimes = (dateRange: [Date | null, Date | null]): DateTimeRange => {
    const [start, end] = dateRange;
    // Use date-fns to get start and end of day in local timezone
    const startWithTime = start ? startOfDay(start) : new Date(); // 00:00:00.000 local time
    const endWithTime = end ? endOfDay(end) : new Date(); // 23:59:59.999 local time

    return { startWithTime, endWithTime };
};

// Helper function to format duration in HH:MM:SS using date-fns
export const formatDuration = (seconds: number | null | undefined): string => {
    if (!seconds || isNaN(seconds)) {
        return '00:00:00';
    }

    // Use date-fns intervalToDuration for better accuracy
    const duration = intervalToDuration({
        start: 0,
        end: seconds * 1000, // Convert seconds to milliseconds
    });

    const hours = duration.hours || 0;
    const minutes = duration.minutes || 0;
    const secs = duration.seconds || 0;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Helper function to format duration for tooltip using date-fns
export const formatDurationTooltip = (seconds: number | null | undefined): string => {
    if (!seconds || isNaN(seconds)) {
        return 'Call lasted 0 seconds';
    }

    // Use date-fns intervalToDuration for consistent parsing
    const duration = intervalToDuration({
        start: 0,
        end: seconds * 1000, // Convert seconds to milliseconds
    });

    const hours = duration.hours || 0;
    const minutes = duration.minutes || 0;
    const secs = duration.seconds || 0;

    const parts: string[] = [];
    if (hours > 0) {
        parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
        parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }
    if (secs > 0 || parts.length === 0) {
        parts.push(`${secs} second${secs !== 1 ? 's' : ''}`);
    }

    return `Call lasted ${parts.join(' and ')}`;
};

/**
 * Returns [startDate, endDate] for a given range type (today, yesterday, last7days, last30days).
 * Returns null for 'custom' or unknown (no preset). Shared by Call and SMS analytics pages.
 */
export const getDateRangeForRangeType = (range: string): [Date, Date] | null => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    switch (range) {
        case 'today':
            return [today, today];
        case 'yesterday':
            return [yesterday, yesterday];
        case 'last7days': {
            const start = new Date(today);
            start.setDate(start.getDate() - 7);
            return [start, today];
        }
        case 'last30days': {
            const start = new Date(today);
            start.setDate(start.getDate() - 30);
            return [start, today];
        }
        case 'next30Days': {
            const start = new Date(today);
            start.setDate(start.getDate() + 30);
            return [today, start];
        }
        case 'custom':
        default:
            return null;
    }
};

/**
 * Builds CSV content from headers and rows with proper escaping, then triggers a file download.
 * Reusable for both Call and SMS analytics export.
 */
export const downloadCsv = (headers: string[], rows: string[][], filename: string): void => {
    const escapeCell = (cell: string): string => (cell ? `"${String(cell).replace(/"/g, '""')}"` : '""');
    const csvContent = [headers.join(','), ...rows.map((row) => row.map(escapeCell).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
